import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PageTitle from "../../layout/PageTitle";
import { getBlogDetails, getAllBlogs, addComment } from "../../apis/BlogApi";
import { toast } from "react-toastify";
import Accordion from "../../common/Accordion";
import Loader from "../../common/Loader";

function BlogDetails({ setTab }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [recentBlogs, setRecentBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentForm, setCommentForm] = useState({
    name: "",
    email: "",
    comment: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [showCommentsDialog, setShowCommentsDialog] = useState(false);
  const [active, setActive] = useState(0);
  const onFAQClick = (currentActive) => {
    setActive(currentActive);
  };

  useEffect(() => {
    setTab("blogs");
    fetchBlogDetails();
    fetchRecentBlogs();
  }, [setTab, id]);

  const fetchBlogDetails = async () => {
    try {
      setLoading(true);
      const response = await getBlogDetails(id);
      setBlog(response);
    } catch (error) {
      console.error("Error fetching blog details:", error);
      toast.error("Failed to load blog details");
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentBlogs = async () => {
    try {
      const response = await getAllBlogs(0, 5);
      setRecentBlogs(response.data || []);
    } catch (error) {
      console.error("Error fetching recent blogs:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCommentForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (!commentForm.name.trim()) {
      toast.error("Please enter your name");
      return false;
    }
    if (!commentForm.email.trim()) {
      toast.error("Please enter your email");
      return false;
    }
    if (!commentForm.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      toast.error("Please enter a valid email address");
      return false;
    }
    if (!commentForm.comment.trim()) {
      toast.error("Please enter your comment");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      await addComment(id, {
        name: commentForm.name,
        email: commentForm.email,
        comment: commentForm.comment,
      });

      toast.success("Comment posted successfully!");
      setCommentForm({
        name: "",
        email: "",
        comment: "",
      });

      // Refresh blog details to show the new comment
      await fetchBlogDetails();
    } catch (error) {
      console.error("Error posting comment:", error);
      toast.error(error?.response?.data?.message || "Failed to post comment");
    } finally {
      setSubmitting(false);
    }
  };

  const handleComments = () => {
    setShowCommentsDialog(true);
  };

  const closeCommentsDialog = () => {
    setShowCommentsDialog(false);
  };

  if (loading) {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          zIndex: 9999,
        }}
      >
        <Loader />
      </div>
    );
  }

  if (!blog) {
    return (
      <>
        <PageTitle page="Blogs" setTab={setTab} />
        <div className="site-main">
          <div className="container">
            <div className="row">
              <div className="col-12 text-center">
                <p>Blog not found</p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageTitle page="Blogs" setTab={setTab} />
      <div className="site-main">
        <div className="ttm-row sidebar ttm-sidebar-right ttm-bgcolor-white clearfix">
          <div className="container">
            <div className="row">
              <div className="col-lg-9 content-area">
                <article className="post ttm-blog-single clearfix">
                  <div className="ttm-post-featured-wrapper ttm-featured-wrapper">
                    <div className="ttm-post-featured">
                      <img
                        className="img-fluid"
                        src={
                          blog?.files?.length
                            ? blog.files[0].url
                            : "https://prasad-eye-hospital-public.s3.ap-south-1.amazonaws.com/checkup.jpg"
                        }
                        alt={blog.title || "Blog Image"}
                      />
                    </div>
                  </div>
                  <div className="ttm-blog-single-content">
                    <div className="ttm-post-entry-header">
                      <div className="post-meta">
                        <span className="ttm-meta-line entry-date">
                          <i className="fa fa-calendar"></i>
                          <time
                            className="entry-date published"
                            dateTime={blog.createdAt}
                          >
                            {new Date(blog.createdAt).toLocaleDateString(
                              "en-US",
                              {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                              }
                            )}
                          </time>
                        </span>
                        <span className="ttm-meta-line byline">
                          <i className="fa fa-user"></i>Admin
                        </span>
                      </div>
                    </div>
                    <div className="entry-content">
                      <div className="ttm-box-desc-text">
                        <h2 className="mb-4">{blog.title}</h2>
                        <div
                          dangerouslySetInnerHTML={{ __html: blog.description }}
                        />

                        {/* FAQ Section */}
                        {blog.faqs?.length &&
                          blog.faqs.map((item, index) => (
                            <Accordion
                              item={item}
                              active={active}
                              onFAQClick={() => onFAQClick(index)}
                              index={index}
                            />
                          ))}

                        <div className="social-media-block">
                          <div className="d-sm-flex justify-content-between">
                            <div className="ttm-social-share-wrapper margin_top15">
                              <h6 className="font-weight-50 margin_bottom0 margin_right10">
                                Share :{" "}
                              </h6>
                              <ul className="social-icons circle">
                                <li>
                                  <a
                                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                                      window.location.href
                                    )}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      window.open(
                                        `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                                          window.location.href
                                        )}`,
                                        "facebook-share",
                                        "width=580,height=296"
                                      );
                                    }}
                                  >
                                    <i className="fa fa-facebook"></i>
                                  </a>
                                </li>
                                <li>
                                  <a
                                    href="#"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      navigator.clipboard.writeText(
                                        window.location.href
                                      );
                                      alert(
                                        "Blog URL copied! You can now paste it in your Instagram story or post."
                                      );
                                    }}
                                    title="Copy link to share on Instagram"
                                  >
                                    <i className="fa fa-instagram"></i>
                                  </a>
                                </li>
                                <li>
                                  <a
                                    href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(
                                      window.location.href
                                    )}&text=${encodeURIComponent(blog.title)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      window.open(
                                        `https://twitter.com/intent/tweet?url=${encodeURIComponent(
                                          window.location.href
                                        )}&text=${encodeURIComponent(
                                          blog.title
                                        )}`,
                                        "twitter-share",
                                        "width=580,height=296"
                                      );
                                    }}
                                  >
                                    <i className="fa fa-twitter"></i>
                                  </a>
                                </li>
                                <li>
                                  <a
                                    href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(
                                      window.location.href
                                    )}&title=${encodeURIComponent(blog.title)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      window.open(
                                        `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(
                                          window.location.href
                                        )}&title=${encodeURIComponent(
                                          blog.title
                                        )}`,
                                        "linkedin-share",
                                        "width=580,height=296"
                                      );
                                    }}
                                  >
                                    <i className="fa fa-linkedin"></i>
                                  </a>
                                </li>
                                <li>
                                  <a
                                    href={`https://api.whatsapp.com/send?text=${encodeURIComponent(
                                      blog.title + " " + window.location.href
                                    )}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    <i className="fa fa-whatsapp"></i>
                                  </a>
                                </li>
                              </ul>
                            </div>
                            <div class="margin_top15">
                              <span
                                onClick={handleComments}
                                style={{ cursor: "pointer" }}
                              >
                                <i className="fa fa-comments ttm-textcolor-skincolor mr-2"></i>
                                {blog.comments?.length || 0}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="ttm-blog-classic-box-comment clearfix">
                          <div id="comments" className="comments-area">
                            <div className="comment-respond">
                              <h3 className="comment-reply-title">
                                Leave a Reply
                              </h3>
                              <form
                                onSubmit={handleSubmit}
                                id="commentform"
                                className="comment-form"
                              >
                                <p className="comment-notes">
                                  Your email address will not be published.{" "}
                                </p>
                                <p className="comment-form-comment">
                                  <textarea
                                    id="comment"
                                    placeholder="Comment"
                                    name="comment"
                                    cols="45"
                                    rows="4"
                                    aria-required="true"
                                    value={commentForm.comment}
                                    onChange={handleInputChange}
                                    disabled={submitting}
                                  ></textarea>
                                </p>
                                <p className="comment-form-author">
                                  <input
                                    id="author"
                                    placeholder="Name (required)"
                                    name="name"
                                    type="text"
                                    value={commentForm.name}
                                    onChange={handleInputChange}
                                    disabled={submitting}
                                    size="30"
                                    aria-required="true"
                                  />
                                </p>
                                <p className="comment-form-email">
                                  <input
                                    id="email"
                                    placeholder="Email (required)"
                                    name="email"
                                    type="email"
                                    value={commentForm.email}
                                    onChange={handleInputChange}
                                    disabled={submitting}
                                    size="30"
                                    aria-required="true"
                                  />
                                </p>
                                <p className="form-submit margin_top30 margin_bottom0">
                                  <button
                                    className="submit ttm-btn ttm-btn-size-md ttm-btn-shape-round ttm-btn-style-fill ttm-btn-color-skincolor"
                                    type="submit"
                                    disabled={submitting}
                                  >
                                    {submitting ? "Posting..." : "Post Comment"}
                                  </button>
                                </p>
                              </form>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </article>
              </div>
              {recentBlogs?.length && (
                <div className="col-lg-3 widget-area sidebar-right">
                  <aside className="widget widget-recent-post with-title">
                    <h3 className="widget-title">Recent Blogs</h3>
                    <ul className="widget-post ttm-recent-post-list">
                      {recentBlogs?.map((recentBlog) => (
                        <li
                          key={recentBlog.id}
                          onClick={() => navigate(`/blogs/${recentBlog.id}`)}
                        >
                          <img
                            src={
                              recentBlog?.files?.length
                                ? recentBlog.files[0].url
                                : "https://prasad-eye-hospital-public.s3.ap-south-1.amazonaws.com/logo.png"
                            }
                            alt={recentBlog.title || "post-img"}
                          />
                          <div className="post-detail">
                            <span className="post-date">
                              <i className="fa fa-calendar"></i>
                              {new Date(
                                recentBlog.createdAt
                              ).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                              })}
                            </span>
                            <a href={`/blogs/${recentBlog.id}`}>
                              {recentBlog.title}
                            </a>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </aside>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Comments Dialog */}
      {showCommentsDialog && (
        <div className="comments-dialog-overlay" onClick={closeCommentsDialog}>
          <div className="comments-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="comments-dialog-header">
              <h3>Comments ({blog.comments?.length || 0})</h3>
              <button className="close-button" onClick={closeCommentsDialog}>
                <i className="fa fa-times-circle"></i>
              </button>
            </div>
            <div className="comments-dialog-content">
              {blog.comments && blog.comments.length > 0 ? (
                blog.comments.map((comment, index) => (
                  <div key={index} className="comment-item">
                    <div className="comment-header">
                      <span className="comment-author">
                        <i className="fa fa-user"></i> {comment.name}
                      </span>
                      <span className="comment-date">
                        <i className="fa fa-calendar"></i>{" "}
                        {new Date(comment.createdAt).toLocaleDateString(
                          "en-US",
                          {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          }
                        )}
                      </span>
                    </div>
                    <div className="comment-content">{comment.comment}</div>
                  </div>
                ))
              ) : (
                <p className="no-comments">
                  No comments yet. Be the first to comment!
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .comments-dialog-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
          padding: 15px;
        }

        .comments-dialog {
          background: white;
          border-radius: 8px;
          width: 100%;
          max-width: 600px;
          max-height: 90vh;
          display: flex;
          flex-direction: column;
          position: relative;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }

        .comments-dialog-header {
          padding: 12px 15px;
          border-bottom: 1px solid #eee;
          display: flex;
          justify-content: space-between;
          align-items: center;
          background-color: #f8f8f8;
          border-radius: 8px 8px 0 0;
        }

        .comments-dialog-header h3 {
          margin: 0;
          color: #333;
          font-size: 16px;
          font-weight: 600;
        }

        .close-button {
          background: none;
          border: none;
          font-size: 20px;
          cursor: pointer;
          color: #666;
          padding: 4px;
          line-height: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: color 0.3s ease;
        }

        .close-button:hover {
          color: #333;
        }

        .close-button i {
          font-size: 24px;
        }

        .comments-dialog-content {
          padding: 15px;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
        }

        .comment-item {
          padding: 12px 0;
          border-bottom: 1px solid #eee;
        }

        .comment-item:last-child {
          border-bottom: none;
        }

        .comment-header {
          display: flex;
          justify-content: space-between;
          margin-bottom: 8px;
          color: #666;
          font-size: 0.85em;
          flex-wrap: wrap;
          gap: 8px;
        }

        .comment-author {
          font-weight: 500;
          color: #333;
        }

        .comment-author i,
        .comment-date i {
          font-size: 12px;
          margin-right: 4px;
        }

        .comment-content {
          color: #444;
          line-height: 1.4;
          font-size: 0.95em;
          word-break: break-word;
        }

        .no-comments {
          text-align: center;
          color: #666;
          padding: 15px;
          font-size: 0.9em;
        }

        .modal-close-icon {
          position: absolute;
          top: 15px;
          right: 15px;
          font-size: 24px;
          color: #666;
          cursor: pointer;
          transition: color 0.3s ease;
          z-index: 1;
        }

        .modal-close-icon:hover {
          color: #333;
        }

        /* Responsive Styles */
        @media screen and (max-width: 768px) {
          .comments-dialog {
            max-height: 95vh;
            margin: 0;
            border-radius: 0;
          }

          .comments-dialog-header {
            padding: 10px;
            border-radius: 0;
          }

          .comments-dialog-header h3 {
            font-size: 15px;
          }

          .comments-dialog-content {
            padding: 12px;
          }

          .comment-header {
            flex-direction: column;
            gap: 4px;
          }

          .comment-date {
            font-size: 0.8em;
          }

          .modal-close-icon {
            top: 10px;
            right: 10px;
            font-size: 20px;
          }
        }

        @media screen and (max-width: 480px) {
          .comments-dialog-overlay {
            padding: 10px;
          }

          .comments-dialog-header h3 {
            font-size: 14px;
          }

          .close-button i {
            font-size: 20px;
          }

          .comment-content {
            font-size: 0.9em;
          }

          .modal-close-icon {
            font-size: 18px;
          }
        }

        /* For very small screens */
        @media screen and (max-height: 500px) {
          .comments-dialog {
            max-height: 100vh;
          }

          .comments-dialog-header {
            padding: 8px 10px;
          }

          .comments-dialog-content {
            padding: 10px;
          }

          .comment-item {
            padding: 8px 0;
          }
        }
      `}</style>
    </>
  );
}

export default BlogDetails;
